import {Brand} from '@mfb/lego';
import {
  GroupedProductSelectorContentModel,
  ProductSelectorContentModel,
  ProductSelectorTabsetEntry,
} from '../ProductSelectorTabSetContentModel';

export interface CurrentSelection {
  selector?: ProductSelectorContentModel;
  groupedEntry?: GroupedProductSelectorContentModel;
}

export const isGroupedProductSelector = (
  entry: ProductSelectorTabsetEntry
): entry is GroupedProductSelectorContentModel =>
  entry.__typename === 'ContentfulGroupedProductSelector';

export const getBrandForEntry = (
  entry: ProductSelectorTabsetEntry
): Brand | 'default' | undefined =>
  isGroupedProductSelector(entry)
    ? entry.productSelectors?.[0]?.brand
    : entry.brand;

export const resolveEntryBrand = (
  entry: ProductSelectorTabsetEntry,
  siteBrand: Brand
): Brand | undefined => {
  const brand = getBrandForEntry(entry);
  return brand === 'default' ? siteBrand : brand;
};

export const getDefaultSelector = (
  groupedEntry: GroupedProductSelectorContentModel
): ProductSelectorContentModel | undefined =>
  groupedEntry.productSelectors?.[0];

export const getEntryHeading = (
  entry: ProductSelectorTabsetEntry
): string | undefined =>
  isGroupedProductSelector(entry)
    ? getDefaultSelector(entry)?.heading
    : entry.heading;

export const flattenSelectors = (
  entries: Array<ProductSelectorTabsetEntry>
): Array<ProductSelectorContentModel> =>
  entries.flatMap(entry => (isGroupedProductSelector(entry) ? entry.productSelectors ?? [] : [entry]));

export const hasRecipeCarouselBelow = (entries: Array<ProductSelectorTabsetEntry>): boolean =>
  flattenSelectors(entries).some(
    selector => selector.recipeCarousel !== undefined && selector.recipeCarouselPosition !== 'Above'
  );

export const resolveCurrentSelector = (
  entries: Array<ProductSelectorTabsetEntry>,
  brand: Brand,
  siteBrand: Brand,
  selectedOptionIdsByGroupId: Record<string, string>
): CurrentSelection => {
  const entry = entries.find(e => resolveEntryBrand(e, siteBrand) === brand);

  if (!entry) {
    return {};
  }

  if (!isGroupedProductSelector(entry)) {
    return {selector: entry};
  }

  const selectedOptionId = selectedOptionIdsByGroupId[entry.id];
  const selector =
    entry.productSelectors?.find(s => s.id === selectedOptionId) ??
    getDefaultSelector(entry);

  return {selector, groupedEntry: entry};
};
